import React, { createContext, useCallback, useContext, useState } from 'react';
import {
  Box,
  Chip,
  Paper,
  Typography,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { daysUntil } from '../utils/formatUtils';

/* ---------- 알림 스낵바 ---------- */

const NotifyContext = createContext(() => {});
export const useNotify = () => useContext(NotifyContext);

export const NotifyProvider = ({ children }) => {
  const [state, setState] = useState(null);
  const notify = useCallback((message, severity = 'success') => setState({ message, severity, key: Date.now() }), []);
  return (
    <NotifyContext.Provider value={notify}>
      {children}
      <Snackbar
        key={state?.key}
        open={!!state}
        autoHideDuration={4000}
        onClose={() => setState(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {state ? (
          <Alert severity={state.severity} variant="filled" onClose={() => setState(null)} sx={{ whiteSpace: 'pre-line' }}>
            {state.message}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </NotifyContext.Provider>
  );
};

/* ---------- 레이아웃 조각 ---------- */

export const PageHeader = ({ title, description, actions }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, gap: 2 }}>
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {description}
        </Typography>
      )}
    </Box>
    {actions && <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>{actions}</Box>}
  </Box>
);

export const StatCard = ({ label, value, sub, color, onClick }) => (
  <Paper
    onClick={onClick}
    sx={{ p: 2, height: '100%', cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { borderColor: 'primary.main' } : {} }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h4" sx={{ fontWeight: 700, my: 0.5, color: color || 'text.primary' }}>
      {value ?? '-'}
    </Typography>
    {sub && (
      <Typography variant="caption" color="text.secondary">
        {sub}
      </Typography>
    )}
  </Paper>
);

/* ---------- 상태 칩 ---------- */

const USER_STATUS = {
  active: { label: '정상', color: 'success' },
  suspended: { label: '정지', color: 'error' },
  withdrawn: { label: '탈퇴', color: 'default' },
};
export const USER_STATUS_OPTIONS = Object.entries(USER_STATUS).map(([value, v]) => ({ value, label: v.label }));

export const UserStatusChip = ({ status }) => {
  const s = USER_STATUS[status || 'active'] || { label: status, color: 'default' };
  return <Chip size="small" label={s.label} color={s.color} variant="outlined" />;
};

const SUB_STATUS = {
  active: { label: '구독중', color: 'primary' },
  expired: { label: '만료', color: 'warning' },
  cancelled: { label: '취소', color: 'default' },
  pending: { label: '입금대기', color: 'info' },
  rejected: { label: '거절', color: 'default' },
  none: { label: '무료', color: 'default' },
};

export const SubscriptionChip = ({ status, planName, expiresAt }) => {
  const s = SUB_STATUS[status] || { label: status || '-', color: 'default' };
  const left = status === 'active' ? daysUntil(expiresAt) : null;
  const label =
    status === 'active' && planName ? `${planName}${left != null ? ` · D-${Math.max(left, 0)}` : ''}` : s.label;
  const color = status === 'active' && left != null && left <= 7 ? 'warning' : s.color;
  return <Chip size="small" label={label} color={color} variant={status === 'active' ? 'filled' : 'outlined'} />;
};

const CLIENT = {
  web: { label: '웹', color: 'info' },
  admin: { label: '관리자', color: 'secondary' },
  agent: { label: '에이전트', color: 'success' },
  app: { label: '앱', color: 'warning' },
  legacy: { label: '기존서버', color: 'default' },
  unknown: { label: '알수없음', color: 'default' },
};

export const ClientChip = ({ client }) => {
  const c = CLIENT[client] || { label: client || '-', color: 'default' };
  return <Chip size="small" label={c.label} color={c.color} variant="outlined" />;
};

const AUDIT_LABEL = {
  USER_UPDATE: '회원정보 수정',
  PASSWORD_RESET: '비밀번호 초기화',
  SESSION_KILL: '세션 종료',
  SESSION_KILL_ALL: '전체 로그아웃',
  TELEGRAM_UNLINK: '텔레그램 해제',
  SUBSCRIPTION_GRANT: '구독 부여',
  SUBSCRIPTION_EXTEND: '구독 기간 변경',
  SUBSCRIPTION_CANCEL: '구독 취소',
  SUBSCRIPTION_APPROVE: '구독 승인',
  SUBSCRIPTION_REJECT: '구독 신청 거절',
  AGENT_REVOKE: '에이전트 폐기',
  PLAN_UPDATE: '요금제 수정',
  AGENT_RELEASE_CREATE: '에이전트 버전 등록',
  AGENT_RELEASE_UPDATE: '에이전트 버전 수정',
  AGENT_RELEASE_DELETE: '에이전트 버전 삭제',
};
export const auditLabel = (action) => AUDIT_LABEL[action] || action;

/* ---------- 확인 다이얼로그 ---------- */

export const ConfirmDialog = ({ open, title, message, confirmText = '확인', danger, icon, onConfirm, onClose, busy }) => (
  <Dialog open={open} onClose={busy ? undefined : onClose}>
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {icon}
      {title}
    </DialogTitle>
    <DialogContent>
      <DialogContentText sx={{ whiteSpace: 'pre-line' }}>{message}</DialogContentText>
    </DialogContent>
    <DialogActions sx={{ pl: 3, pr: 4, pb: 2.5 }}>
      <Button onClick={onClose} disabled={busy}>
        취소
      </Button>
      <Button variant="contained" color={danger ? 'error' : 'primary'} onClick={onConfirm} disabled={busy}>
        {confirmText}
      </Button>
    </DialogActions>
  </Dialog>
);

/** confirm 상태를 한 번에 다루는 훅: const [dialog, ask] = useConfirm(); ask({ title, message, onConfirm }) */
export const useConfirm = () => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const ask = useCallback((opts) => setState(opts), []);
  const dialog = (
    <ConfirmDialog
      open={!!state}
      title={state?.title}
      message={state?.message}
      confirmText={state?.confirmText}
      danger={state?.danger}
      icon={state?.icon}
      busy={busy}
      onClose={() => setState(null)}
      onConfirm={async () => {
        setBusy(true);
        try {
          await state?.onConfirm?.();
        } finally {
          setBusy(false);
          setState(null);
        }
      }}
    />
  );
  return [dialog, ask];
};
